import * as Styles from './phoneticsmodal.styles';
import * as React from 'react';

// Checks whether the text can be split into accepted values. A regex alternation of the values is not used, because values
// overlap (e.g. 'e' is in both lists, and 'eː' is also 'e' + 'ː'), which makes rejecting a long text backtrack exponentially
export const hasUnacceptedValues = (phoneticsText: string) => {
  // isSplittable[i] is true when the first i characters can be split into accepted values
  const isSplittable = new Array<boolean>(phoneticsText.length + 1).fill(false);
  isSplittable[0] = true;

  for (let position = 0; position < phoneticsText.length; position++) {
    if (isSplittable[position]) {
      for (const value of PHONETIC_TEXT_VALUES) {
        if (phoneticsText.startsWith(value, position)) {
          isSplittable[position + value.length] = true;
        }
      }
    }
  }

  return !isSplittable[phoneticsText.length];
};

// Say-as text is a plain-text alias that is read aloud, so allow any letters (incl. æ, ø, å), digits, whitespace and basic punctuation
// Keep the say-as error message in PhoneticEditor in sync when changing the allowed characters
const SAY_AS_TEXT_REGEX = /^[\p{L}\p{M}\p{N}\s.,'-]*$/u;

export const sayAsHasUnacceptedValues = (sayAsText: string) => !SAY_AS_TEXT_REGEX.test(sayAsText);

const ACCEPTED_VALUES = [
  'ɑ',
  'ɑː',
  'ɛ',
  'ɛː',
  'ɔ',
  'ɒ',
  'ɒː',
  'ɔː',
  'ɐ',
  'æː',
  'e',
  'ø',
  'øː',
  'ə',
  'eː',
  'i',
  'iː',
  'o',
  'œ',
  'œː',
  'oː',
  'u',
  'uː',
  'y',
  'yː',
  'b',
  'd',
  'ð',
  'f',
  'g',
  'h',
  'j',
  'kʰ',
  'l',
  'm',
  'n',
  'ŋ',
  'pʰ',
  'ʔ',
  'ʁ',
  'ɐ̯',
  's',
  'ɕ',
  't',
  'v',
  'w',
];

const ENGLISH_ALPHABET = [
  'a',
  'e',
  'i',
  'o',
  'u',
  'b',
  'c',
  'd',
  'f',
  'g',
  'h',
  'j',
  'k',
  'l',
  'm',
  'n',
  'p',
  'q',
  'r',
  's',
  't',
  'v',
  'w',
  'x',
  'y',
  'z',
];

const SUPPORT_CHARACTERS = ['ˈ', 'ˌ', '.', 'ː', '‿'];

const ALL_PHONETIC_CHARACTERS = ACCEPTED_VALUES.concat(SUPPORT_CHARACTERS);

const PHONETIC_TEXT_VALUES = ALL_PHONETIC_CHARACTERS.concat(ENGLISH_ALPHABET);

const handleClick = (event: React.MouseEvent<HTMLElement>, letter: string, callBack?: (character: string) => void) => {
  // Clear all current selections
  window.getSelection()?.removeAllRanges();

  const range = document.createRange();

  if (event.target instanceof Node) {
    range.selectNode(event.target);

    // Add the Range to the set of window selections
    window.getSelection()?.addRange(range);
  }
  callBack?.(letter);
};

const createLetters = (
  start: number,
  end: number,
  onClick: (event: React.MouseEvent<HTMLElement>, letter: string, callBack?: (character: string) => void) => void,
  passToEditor?: (character: string) => void,
) =>
  ALL_PHONETIC_CHARACTERS.reduce((acc, v, i) => {
    if (i >= start && i < end) {
      acc.push(
        <Styles.PhoneticLetter key={i} onClick={(e: React.MouseEvent<HTMLElement>) => onClick(e, v, passToEditor)}>
          {v}
        </Styles.PhoneticLetter>,
      );
    }
    return acc;
  }, [] as JSX.Element[]);

export const accepted_vowels = (passToEditor?: (character: string) => void) => createLetters(0, 25, handleClick, passToEditor);
export const accepted_consonants = (passToEditor?: (character: string) => void) =>
  createLetters(25, ACCEPTED_VALUES.length, handleClick, passToEditor);
export const accepted_support_characters = (passToEditor?: (character: string) => void) =>
  createLetters(ACCEPTED_VALUES.length, ALL_PHONETIC_CHARACTERS.length, handleClick, passToEditor);
